/**
 * Pure utility functions that have NO Bryntum dependencies.
 * These can be safely imported by non-Gantt code without pulling in Bryntum.
 */

import { colors } from "@procore/core-react";

/**
 * Determines the appropriate text color (black or white) for a given background color
 * to ensure readable contrast.
 */
export function getTextColorForBackground(background: string | undefined) {
   if (!background) return colors.black;
   if (background === colors.white) return colors.black;
   if (background === colors.black) return colors.white;

   // Remove the hash if it's there
   const hex = background.replace("#", "");

   // Parse the r, g, b values
   const r = parseInt(hex.substring(0, 2), 16);
   const g = parseInt(hex.substring(2, 4), 16);
   const b = parseInt(hex.substring(4, 6), 16);

   // Calculate the brightness
   const brightness = (r * 299 + g * 587 + b * 114) / 1000;

   // Return black or white depending on the brightness
   return brightness > 128 ? colors.black : colors.white;
}

/**
 * Lightens a hex color by a percentage amount towards white.
 */
export function lightenHexColor(hex: string, amount: number) {
   if (!hex) return;

   if (hex.indexOf("#") !== 0) {
      hex = "#" + hex;
   }

   const num = parseInt(hex.slice(1), 16);
   let r = num >> 16;
   let g = (num >> 8) & 0x00ff;
   let b = num & 0x0000ff;

   // Scale each component by the percentage amount towards 255
   r = Math.floor(r + (255 - r) * amount);
   g = Math.floor(g + (255 - g) * amount);
   b = Math.floor(b + (255 - b) * amount);

   // Combine back into a hex string
   return "#" + ((r << 16) | (g << 8) | b).toString(16).padStart(6, "0");
}

/**
 * Returns an array of color shades: [base, light (40%), lightest (80%)]
 */
export function getColorShades(color: string) {
   const baseColor = color;
   const lightColor = lightenHexColor(baseColor, 0.4) ?? baseColor;
   const lightestColor = lightenHexColor(baseColor, 0.8) ?? baseColor;

   return [baseColor, lightColor, lightestColor];
}

/**
 * Converts a float time to a string (e.g., 14.5 to "14:30:00")
 */
export const timeFloatToString = (time: number | null | undefined): string | undefined => {
   if (typeof time === "number") {
      const hours = Math.floor(time).toString().padStart(2, "0");
      const minutes = Math.floor((time % 1) * 60)
         .toString()
         .padStart(2, "0");
      return `${hours}:${minutes}:00`;
   }
   return time ?? undefined;
};

/**
 * Parses a time string (HH:MM:SS) to hours as a decimal number.
 */
export function parseTimeToHours(timeString: string): number {
   const [hours, minutes] = timeString.split(":").map(Number);
   return hours + minutes / 60;
}
